import React, { useEffect, useState } from 'react';
import { FaSlack } from 'react-icons/fa';
import { LuCircleCheck, LuLoader } from 'react-icons/lu';

import { getSlackAuthUrl } from '../../shared/api/integration';

interface Props {
  isConnected: boolean;
  workspaceId?: string;
  linkedAt?: string;
  onRefresh: () => void;
  onDisconnect: () => Promise<void>;
}

const SlackIntegration: React.FC<Props> = ({ isConnected, workspaceId, linkedAt, onRefresh, onDisconnect }) => {
  const [waiting, setWaiting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!waiting) return;
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'slack-connected') {
        setWaiting(false);
        onRefresh();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [waiting, onRefresh]);

  const handleConnect = async () => {
    const { url } = await getSlackAuthUrl();
    window.open(url, '_blank', 'width=600,height=700');
    setWaiting(true);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    setError(null);
    try {
      await onDisconnect();
    } catch {
      setError('Failed to disconnect Slack. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleCancel = () => {
    setWaiting(false);
  };

  if (isConnected) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <LuCircleCheck className="text-success text-xl shrink-0" />
            <div>
              <div className="text-sm font-medium">Connected</div>
              {workspaceId && <div className="text-xs opacity-60">Workspace ID: {workspaceId}</div>}
              {linkedAt && <div className="text-xs opacity-60">Since {new Date(linkedAt).toLocaleDateString()}</div>}
            </div>
          </div>
          <button className="btn btn-sm btn-outline btn-error" onClick={handleDisconnect} disabled={disconnecting}>
            {disconnecting ? <LuLoader className="animate-spin" /> : 'Disconnect'}
          </button>
        </div>
        {error && <div className="text-error text-sm">{error}</div>}
      </div>
    );
  }

  if (waiting) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm opacity-70">
          <LuLoader className="animate-spin shrink-0" />
          Complete the Slack authorization in the popup window…
        </div>
        <button className="btn btn-sm btn-ghost" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm opacity-70">
        Connect your Slack workspace to receive AI-powered project summaries and notifications directly in Slack.
      </div>
      <button className="btn btn-sm btn-primary gap-2" onClick={handleConnect}>
        <FaSlack />
        Connect Slack
      </button>
    </div>
  );
};

export default SlackIntegration;
